'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { Boat } from '@/types';

interface CompareCtx {
  selected: Boat[];
  isSelected: (id: string) => boolean;
  toggle: (boat: Boat) => void;
  clear: () => void;
  remove: (id: string) => void;
  /** Apre overlay confronto. */
  openModal: () => void;
  /** Chiude overlay. */
  closeModal: () => void;
  modalOpen: boolean;
}

const Ctx = createContext<CompareCtx | null>(null);

const STORAGE_KEY = 'insulacharter:compare';
const MAX = 4;

export function CompareProvider({ children }: { children: React.ReactNode }) {
  const [selected, setSelected] = useState<Boat[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setSelected(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(selected)); } catch {}
  }, [selected]);

  const value: CompareCtx = {
    selected,
    isSelected: (id) => selected.some((b) => b.id === id),
    toggle: (boat) => {
      setSelected((prev) => {
        const has = prev.some((b) => b.id === boat.id);
        if (has) return prev.filter((b) => b.id !== boat.id);
        if (prev.length >= MAX) return prev; // limite
        return [...prev, boat];
      });
    },
    clear: () => setSelected([]),
    remove: (id) => setSelected((prev) => prev.filter((b) => b.id !== id)),
    openModal: () => setModalOpen(true),
    closeModal: () => setModalOpen(false),
    modalOpen,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCompare() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCompare must be used within CompareProvider');
  return ctx;
}

/** Variante no-throw: ritorna null se non c'è CompareProvider (es. vetrina). */
export function useCompareOptional() {
  return useContext(Ctx);
}

export const COMPARE_MAX = MAX;
